import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Download } from "../../../assets/icons/download.svg";
import { ReactComponent as LeftArrow } from "../../../assets/icons/leftArrow.svg";
import Dropdown from "../../Common/Dropdown";

import { AxiosError } from "axios";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../app/store";
import { BarData } from "../../../interface/Trends";
import {
  formatAndSetDataForOverallAnalysis,
  handleDownload,
} from "../../../utils/trendSidebarGraphUtils/overallAnalysisUtils";
import OverallAnalysisGraph from "./OverallAnalysisGraph";
import OverallAnalysisTable from "./OverallAnalysisTable";
import { filterBy } from "../../../constants/constants";
import { useLocation } from "react-router-dom";
import { getTrendFiltersForGraphs } from "../../../utils/trendSidebarGraphUtils/graphHelperUtils";
import { getKeyFromValueForTrendQueryType } from "../../../utils/commonUtils";
interface ExpandViewOverallAnalysisProps {
  isSidebarOpen?: boolean;
}

const ExpandViewOverallAnalysis: React.FC<ExpandViewOverallAnalysisProps> = ({
  isSidebarOpen,
}) => {
  const navigate = useNavigate();
  const [trendFilterType, setTrendFilterType] = useState("Most Popular");
  const [sortingType, setSortingType] = useState("Trend Score");
  const [loading, setLoading] = useState<boolean>(false);

  const [dynamicColumns, setDynamicColumns] = useState<string[]>([]);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const [barData, setBarData] = useState<BarData[] | null>(null);

  const graphContainerRef = useRef<HTMLDivElement | null>(null);

  const user = useSelector((state: RootState) => state.user.userInfo);
  const graphRevision = useSelector(
    (state: RootState) => state.trends.graphRevision
  );

  const location = useLocation();
  const { selectedMaterials } = location.state || {};
  const fetchBarGraphData = async () => {
    try {
      setLoading(true);
      const trendFilters = getTrendFiltersForGraphs();
      let newTrendFilterType = trendFilterType;
      if (trendFilters?.query_type) {
        newTrendFilterType = getKeyFromValueForTrendQueryType(
          trendFilters.query_type
        );
        setTrendFilterType(newTrendFilterType);
      }
      await formatAndSetDataForOverallAnalysis(
        newTrendFilterType,
        user,
        setBarData,
        setDynamicColumns,
        sortingType
      );
    } catch (error) {
      console.error("Error Fetching bar graph data", error);
      if ((error as AxiosError)?.response?.status !== 400) {
        toast.error("Error Fetching bar graph data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBarGraphData();
  }, [trendFilterType, sortingType, graphRevision]);

  if (barData && barData.length === 0) {
    return <div className="my-10 text-center">No Bar Data</div>;
  }

  return (
    <div
      className={`bg-white fixed h-[calc(100vh-80px)]  ml-2 ${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} transition-all duration-500 ease-in-out`}
    >
      <div className="flex flex-row justify-between mb-4">
        <div className="flex flex-row mt-6">
          <div>
            <LeftArrow
              className="text-lg ml-4 mt-2 cursor-pointer"
              onClick={() => navigate("/designer-page")}
            />
          </div>
          <div className="transition-all  duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-4 mt-0.5 text-secondary ">
            Overall Analysis
          </div>
        </div>
        <div className="mt-2 mr-4 flex items-end">
          <div>
            <button
              onClick={() =>
                handleDownload(
                  setDownloadLoading,
                  barData,
                  dynamicColumns,
                  selectedMaterials,
                  graphContainerRef.current ?? undefined
                )
              }
              disabled={downloadLoading}
              title="Download Overall Analysis Report"
              className="flex items-center gap-2 mr-5 mt-1 border border-gray-200 rounded-md px-3 py-1.5 bg-white text-secondary hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {downloadLoading ? (
                <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
              ) : (
                <Download width={20} height={20} />
              )}
            </button>
          </div>

          <div className="flex flex-row gap-4 mt-2">
            <div className="flex-col ">
              <div className="text-xs">View By</div>
              <div className="w-48 mt-1">
                <Dropdown
                  label={sortingType || "Select type"}
                  options={filterBy}
                  onSelect={option => {
                    if (option !== sortingType) setSortingType(option);
                  }}
                />
              </div>
            </div>
            <div>
              <div className="text-sm">Filter By</div>
              <div className="w-40">
                <div className="mt-0 block w-full pl-3 pr-3 py-1 text-left text-base border border-default rounded-lg bg-gray-50">
                  <span className="text-secondary text-xs capitalize">
                    {trendFilterType || "Select type"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-auto h-[calc(100vh-150px)] pb-4">
        {!barData || loading ? (
          <div className="my-10 flex items-center justify-center">
            <Skeleton height={350} width={1200} />
          </div>
        ) : (
          <div
            ref={graphContainerRef}
            className="mt-2 mb-6 flex justify-center items-start"
          >
            <OverallAnalysisGraph
              barData={barData}
              expanded={true}
              selectedMaterials={selectedMaterials}
            />
          </div>
        )}

        {!barData || loading ? (
          <div className="my-10  flex items-center justify-center">
            {/* Generate the skeleton rows */}
            {Array.from({ length: 8 }).map((_, index) => (
              <div>
                {Array.from({ length: 7 }).map((_, colIndex) => (
                  <Skeleton
                    key={`${index}-${colIndex}`}
                    width={100}
                    className="mx-4"
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <OverallAnalysisTable
            dynamicColumns={dynamicColumns}
            barData={barData}
            selectedMaterials={selectedMaterials}
          />
        )}
      </div>
    </div>
  );
};

export default ExpandViewOverallAnalysis;
